
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import blogImg from "../assests/images/blog-bg.jpg";
import axios from "axios";
import { Helmet } from "react-helmet-async";

import {
  FaArrowRight,
  FaCalendarAlt,
  FaClock,
  FaSearch,
  FaTimes,
} from "react-icons/fa";

export default function Blogs() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  useEffect(() => {
    axios
      .get("https://beachhoteldubai.com/api/blogs")
      .then((res) => {
        const data = res.data.blogs || res.data;
        setBlogs(data);

        const uniqueCategories = [
          ...new Map(
            data
              .filter((blog) => blog.category)
              .map((blog) => [
                blog.category.id,
                blog.category,
              ])
          ).values(),
        ];

        setCategories(uniqueCategories);
      })
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const filteredBlogs = blogs.filter((blog) => {
    const matchesSearch = blog.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesCategory =
      !selectedCategory ||
      String(blog.category?.id) === String(selectedCategory);

    return matchesSearch && matchesCategory;
  });

  const clearFilters = () => {
    setSearch("");
    setSelectedCategory("");
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getImage = (blog) =>
    blog.image
      ? `https://beachhoteldubai.com/uploads/blogs/${blog.image}`
      : blogImg;

  return (
    <>
      <Helmet>
        <title>Travel Blog | Dubai Marina</title>
        <meta
          name="description"
          content="Explore Dubai travel guides, hotel tips, destination inspiration and travel stories."
        />
        <link
          rel="canonical"
          href="https://yourdomain.com/blogs"
        />
        <meta property="og:title" content="Travel Blog | Dubai Marina" />
        <meta
          property="og:description"
          content="Discover travel inspiration and hotel guides for your Dubai getaway."
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* HERO */}
      <Hero
        title="Travel Stories"
        subtitle="Discover travel inspiration, destination guides and stories for your next Dubai getaway."
        backgroundImage={blogImg}
      />

      {/* BLOG CONTENT */}
      <section className="relative w-full overflow-hidden bg-[#F3F8FF] px-5 py-14 sm:px-8 sm:py-20 lg:px-12 lg:py-24">

        {/* Background decoration */}
        <div className="pointer-events-none absolute -left-32 top-20 h-72 w-72 rounded-full bg-[#D7A74A]/10 blur-3xl" />
        <div className="pointer-events-none absolute -right-32 bottom-20 h-80 w-80 rounded-full bg-[#8B0000]/5 blur-3xl" />

        <div className="relative mx-auto w-full max-w-[1310px]">

          {/* INTRO */}
          <div className="mb-10 flex flex-col gap-5 lg:mb-12 lg:flex-row lg:items-end lg:justify-between">

            <div className="max-w-[650px]">
              <div className="mb-4 flex items-center gap-3">
                <span className="h-px w-9 bg-[#D7A74A]" />
                <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#8B0000]">
                  The Dubai Marina Journal
                </span>
              </div>

              <h2 className="font-serif text-[32px] font-medium leading-[1.12] tracking-[-0.025em] text-[#0B5552] sm:text-[40px] lg:text-[48px]">
                Stories that inspire
                <span className="italic text-[#8B0000]">
                  {" "}your next journey.
                </span>
              </h2>

              <p className="mt-4 max-w-[570px] text-[13px] leading-6 text-[#71808A] sm:text-[14px] sm:leading-7">
                Explore Dubai through our travel guides,
                hotel recommendations, local experiences
                and carefully curated stories.
              </p>
            </div>

            <div className="flex items-center gap-3 rounded-full border border-[#D7A74A]/30 bg-white px-5 py-3 shadow-[0_8px_25px_rgba(11,85,82,0.06)]">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#8B0000] text-white">
                <FaArrowRight />
              </div>
              <div>
                <p className="text-[11px] font-bold text-[#0B5552]">
                  Travel Inspiration
                </p>
                <p className="mt-0.5 text-[10px] text-[#71808A]">
                  Guides • Stories • Experiences
                </p>
              </div>
            </div>
          </div>

          {/* SEARCH AND FILTERS */}
          <div className="mb-10 rounded-[22px] border border-[#E2E9EE] bg-white p-4 shadow-[0_8px_30px_rgba(11,85,82,0.05)] sm:p-5">

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

              <div className="relative w-full lg:max-w-[380px]">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-[#71808A]" />

                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search travel stories..."
                  className="w-full rounded-xl border border-[#E2E9EE] bg-[#F3F8FF] py-3.5 pl-11 pr-4 text-[12px] text-[#0B5552] outline-none transition focus:border-[#D7A74A] focus:ring-2 focus:ring-[#D7A74A]/15"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => setSelectedCategory("")}
                  className={`rounded-full px-4 py-2.5 text-[11px] font-semibold transition ${selectedCategory === ""
                    ? "bg-[#0B5552] text-white shadow-md"
                    : "border border-[#E2E9EE] bg-white text-[#71808A] hover:border-[#D7A74A] hover:text-[#8B0000]"
                    }`}
                >
                  All Stories
                </button>

                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() =>
                      setSelectedCategory(String(category.id))
                    }
                    className={`rounded-full px-4 py-2.5 text-[11px] font-semibold transition ${String(selectedCategory) === String(category.id)
                      ? "bg-[#0B5552] text-white shadow-md"
                      : "border border-[#E2E9EE] bg-white text-[#71808A] hover:border-[#D7A74A] hover:text-[#8B0000]"
                      }`}
                  >
                    {category.name}
                  </button>
                ))}

                {(search || selectedCategory) && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-[#8B0000] hover:underline"
                  >
                    <FaTimes />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* RESULTS HEADER */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h3 className="font-serif text-[22px] font-medium text-[#0B5552]">
                {search || selectedCategory
                  ? "Search Results"
                  : "Latest Stories"}
              </h3>
              <p className="mt-1 text-[11px] text-[#71808A]">
                Discover something worth exploring.
              </p>
            </div>

            <span className="rounded-full border border-[#D7A74A]/30 bg-white px-4 py-2 text-[10px] font-bold text-[#0B5552]">
              {filteredBlogs.length} Articles
            </span>
          </div>

          {/* LOADING */}
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <div
                  key={item}
                  className="overflow-hidden rounded-[22px] border border-[#E2E9EE] bg-white"
                >
                  <div className="h-[230px] animate-pulse bg-[#E8EFF5]" />
                  <div className="space-y-4 p-6">
                    <div className="h-3 w-24 animate-pulse rounded bg-[#E8EFF5]" />
                    <div className="h-5 w-full animate-pulse rounded bg-[#E8EFF5]" />
                    <div className="h-3 w-4/5 animate-pulse rounded bg-[#E8EFF5]" />
                    <div className="h-3 w-2/3 animate-pulse rounded bg-[#E8EFF5]" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredBlogs.length > 0 ? (
            <>
              {/* FEATURED ARTICLE */}
              <Link
                to={`/blogs/${filteredBlogs[0].slug}`}
                className="group mb-8 grid overflow-hidden rounded-[26px] border border-[#E2E9EE] bg-white shadow-[0_12px_40px_rgba(11,85,82,0.07)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,85,82,0.12)] lg:grid-cols-2"
              >
                <div className="relative min-h-[280px] overflow-hidden sm:min-h-[360px]">
                  <img
                    src={getImage(filteredBlogs[0])}
                    alt={filteredBlogs[0].title}
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />

                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B5552]/60 via-transparent to-transparent" />

                  <span className="absolute left-5 top-5 rounded-full bg-[#D7A74A] px-4 py-2 text-[9px] font-bold uppercase tracking-[0.15em] text-[#0B5552]">
                    Featured Story
                  </span>
                </div>

                <div className="flex flex-col justify-center bg-white p-7 sm:p-10 lg:p-12">
                  <div className="mb-5 flex flex-wrap items-center gap-3 text-[10px] text-[#71808A]">
                    <span className="rounded-full bg-[#F8F5EF] px-3 py-1.5 font-semibold text-[#8B0000]">
                      {filteredBlogs[0].category?.name || "Travel"}
                    </span>

                    <span className="flex items-center gap-1.5">
                      <FaCalendarAlt className="text-[#D7A74A]" />
                      {formatDate(filteredBlogs[0].created_at)}
                    </span>
                  </div>

                  <h3 className="font-serif text-[25px] font-medium leading-tight text-[#0B5552] transition-colors duration-300 group-hover:text-[#8B0000] sm:text-[32px] lg:text-[38px]">
                    {filteredBlogs[0].title}
                  </h3>

                  <p className="mt-4 line-clamp-3 text-[12px] leading-6 text-[#71808A] sm:text-[13px] sm:leading-7">
                    {filteredBlogs[0].excerpt ||
                      filteredBlogs[0].description ||
                      "Discover travel inspiration, destination highlights and useful tips for your next stay."}
                  </p>

                  <div className="mt-7 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.12em] text-[#8B0000]">
                    Read Full Story
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F8F5EF] transition-all duration-300 group-hover:bg-[#8B0000] group-hover:text-white">
                      <FaArrowRight />
                    </span>
                  </div>
                </div>
              </Link>

              {/* ARTICLE GRID */}
              {filteredBlogs.length > 1 && (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredBlogs.slice(1).map((blog) => (
                    <Link
                      key={blog.id}
                      to={`/blogs/${blog.slug}`}
                      className="group overflow-hidden rounded-[22px] border border-[#E2E9EE] bg-white shadow-[0_8px_30px_rgba(11,85,82,0.05)] transition-all duration-500 hover:-translate-y-1.5 hover:border-[#D7A74A]/50 hover:shadow-[0_18px_40px_rgba(11,85,82,0.11)]"
                    >
                      <div className="relative h-[230px] overflow-hidden">
                        <img
                          src={getImage(blog)}
                          alt={blog.title}
                          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />

                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B5552]/40 via-transparent to-transparent opacity-70" />

                        <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3 py-1.5 text-[9px] font-bold uppercase tracking-wider text-[#8B0000] shadow-sm">
                          {blog.category?.name || "Travel"}
                        </span>
                      </div>

                      <div className="p-5 sm:p-6">
                        <div className="mb-3 flex items-center gap-2 text-[10px] text-[#71808A]">
                          <FaCalendarAlt className="text-[#D7A74A]" />
                          {formatDate(blog.created_at)}
                        </div>

                        <h3 className="line-clamp-2 font-serif text-[19px] font-medium leading-snug text-[#0B5552] transition-colors duration-300 group-hover:text-[#8B0000]">
                          {blog.title}
                        </h3>

                        <p className="mt-3 line-clamp-3 text-[11px] leading-6 text-[#71808A]">
                          {blog.excerpt ||
                            blog.description ||
                            "Explore helpful travel tips, destination inspiration and ideas for your next Dubai stay."}
                        </p>

                        <div className="mt-5 flex items-center justify-between border-t border-[#E2E9EE] pt-4">
                          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8B0000]">
                            Read Article
                          </span>

                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F8F5EF] text-[#0B5552] transition-all duration-300 group-hover:bg-[#8B0000] group-hover:text-white">
                            <FaArrowRight className="text-[11px]" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            /* EMPTY STATE */
            <div className="rounded-[26px] border border-[#E2E9EE] bg-white px-6 py-16 text-center shadow-[0_8px_30px_rgba(11,85,82,0.05)]">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#F8F5EF] text-[#8B0000]">
                <FaSearch className="text-xl" />
              </div>

              <h3 className="mt-5 font-serif text-[24px] font-medium text-[#0B5552]">
                No stories found
              </h3>

              <p className="mx-auto mt-2 max-w-[360px] text-[12px] leading-6 text-[#71808A]">
                We couldn't find any articles matching your
                search. Try another keyword or category.
              </p>

              <button
                onClick={clearFilters}
                className="mt-6 rounded-full bg-[#8B0000] px-6 py-3 text-[11px] font-bold text-white transition hover:bg-[#0B5552]"
              >
                Clear Filters
              </button>
            </div>
          )}

          {/* BOTTOM CTA */}
          {!loading && filteredBlogs.length > 0 && (
            <div className="mt-12 overflow-hidden rounded-[24px] bg-[#0B5552] px-6 py-9 text-center shadow-[0_15px_40px_rgba(11,85,82,0.13)] sm:px-10 sm:py-12">
              <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#D7A74A]">
                Your Next Dubai Story
              </span>

              <h3 className="mx-auto mt-3 max-w-[600px] font-serif text-[25px] font-medium leading-tight text-white sm:text-[34px]">
                Let your next journey begin with the right stay.
              </h3>

              <p className="mx-auto mt-3 max-w-[500px] text-[12px] leading-6 text-white/70">
                Explore stays and discover more of what Dubai has to offer.
              </p>

              <Link
                to="/hotels"
                className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#D7A74A] px-6 py-3.5 text-[11px] font-bold text-[#0B5552] transition hover:bg-white"
              >
                Explore Hotels
                <FaArrowRight />
              </Link>
            </div>
          )}
        </div>
      </section>
    </>
  );
}